import { pixelMask, pixelRect } from './tilePixels';
import type * as PIXI from 'pixi.js';
import type { TileMeta, ConnectionFlags } from '@/models/tiles/TileDefinition';
import type { TileInstance } from '@/models/tiles/TileInstance';
import { defaultTileGraphics } from '@/models/tiles/DefaultTileGraphics';
import { cableActivityColor, bundleWireColor, bundleLowWireColor } from './drawBundle';

type InterfaceData = {
  meta: TileMeta;
  state: { signalId?: string; bit?: number; connectedInterface?: unknown };
  connections: ConnectionFlags;
};
const disconnected: ConnectionFlags = {
  top: false,
  right: false,
  bottom: false,
  left: false,
};

const portMask = [
  '.....###############',
  '....################',
  '...#################',
  '..##################',
  '..##################',
  '.###################',
  '.###################',
  '.###################',
  '####################',
  '####################',
  '####################',
  '####################',
  '####################',
  '####################',
  '####################',
  '.###################',
  '.###################',
  '.###################',
  '..##################',
  '..##################',
  '...#################',
  '....################',
  '.....###############',
];
function portBody(g: PIXI.Graphics, color: number) {
  pixelMask(g, portMask, 2, 1, color);
  pixelMask(
    g,
    [
      '....############',
      '...#############',
      '..##############',
      '.###############',
      '.###############',
      '.###############',
      '################',
      '################',
      '################',
      '################',
      '################',
      '################',
      '################',
      '.###############',
      '.###############',
      '.###############',
      '..##############',
      '...#############',
      '....############',
    ],
    4,
    3,
    0x454545,
  );
}
function drawPort(g: PIXI.Graphics, connections: ConnectionFlags, activity = 0) {
  defaultTileGraphics(g);
  portBody(g, 0x969696);
  if (connections.left) {
    pixelRect(g, 0, 10, 4, 5, cableActivityColor(activity));
    pixelRect(g, 0, 12, 4, 1, 0x2a1112);
  }
}

/** All palette states use the same disconnected port, including an empty draft. */
export function drawBundleInterface(g: PIXI.Graphics, _data: InterfaceData) {
  drawPort(g, disconnected);
}

export function bundleInterfaceDisplay(tile: TileInstance) {
  const signalId = (tile.state as { signalId?: string }).signalId;
  const selectedIndex = signalId
    ? (tile.laneSignalIds?.left ?? []).findIndex((ids) => ids.includes(signalId))
    : -1;
  return {
    selectedIndex,
    label: signalId ? (tile.bundleSignalLabels?.[signalId] ?? '?') : '?',
  };
}

/** Physical connection flags, never forced metadata, control visible cable stubs. */
export function drawBundleInterfaceInstance(
  g: PIXI.Graphics,
  tile: TileInstance,
  actualConnections: ConnectionFlags = disconnected,
) {
  const input = tile.laneInputs?.left ?? [];
  const output = tile.laneOutputs?.left ?? [];
  const values = (tile.laneNodeIds?.left ?? []).map((_, lane) =>
    Boolean(input[lane] || output[lane]),
  );
  const activity = values.length ? values.filter(Boolean).length / values.length : 0;
  drawPort(g, actualConnections, activity);
  const state = tile.state as InterfaceData['state'];
  const legacyScalar =
    !tile.adapterPort &&
    !state.connectedInterface &&
    (state.signalId !== undefined || state.bit !== undefined);
  if (legacyScalar && actualConnections.right) {
    const selected = bundleInterfaceDisplay(tile);
    const high = selected.selectedIndex >= 0 && Boolean(values[selected.selectedIndex]);
    pixelRect(g, 22, 12, 3, 1, high ? bundleWireColor : bundleLowWireColor);
  }
  if (tile.bundleErrors.length) portBody(g, 0xff4444);
}
